// src/view/tutorialHud.ts — Eğitim modu paneli (docs/TUTORIAL.md §4–§5). Kendi kökünü ve tek <style>'ını programatik kurar
// (index.html'e dokunmaz; hud.ts'in stil dili: sarı #ffd43b vurgusu, koyu yarı saydam kutu, system-ui 14px).
// Panel SUNUM katmanıdır: olay üretmez, girdi dinlemez, zamanlayıcısı yalnız kendi animasyonlarını (kutlama / ipucu / geçiş)
// yönetir — ders ilerlemesi ve tuşlar oyun katmanının işidir (TUTORIAL.md §6). `pointer-events:none`: panel tıklamayı da yutmaz.
// Yerleşim (§4): sol alt, 24 px boşluk, min(420px, %34), ≈190 px; dar ekranda (< 900 px) alt ortada, HUD kartlarının üstünde (100 px).
// Ölçü birimi yasağı (§6) panelin metinlerine değil, İÇERİĞE aittir: bu dosya metin üretmez, yalnız verileni yazar.

/** Bir tuş öbeği: aynı hamlenin klavye ve gamepad karşılığı ("J · J · J" / "X · X · X").
 *  Panel ikisini birden GÖSTERMEZ: yalnız bağlı girdininki çizilir (TUTORIAL §4). */
export interface TutorialKeyHint {
  /** Klavye rozetleri, basılacak sırayla: ['J','J','J'] · ['W','A','S','D']. */
  readonly kb: readonly string[];
  /** Gamepad karşılığı: ['X','X','X']. Yoksa (pad bağlı olsa bile) klavye rozeti çizilir — öbek boş kalmasın. */
  readonly pad?: readonly string[];
  /** Rozetlerin önündeki kısa etiket: "hafif", "tut". */
  readonly label?: string;
  /** true → rozetlerin ardına "basılı tut" notu (blok / hızlı iniş dersleri). */
  readonly hold?: boolean;
}
/** Kısa yazım: düz metin = tek klavye rozeti. */
export type TutorialKeySpec = TutorialKeyHint | string;

/** Panelin bir ders hakkında ihtiyacı olan HER ŞEY (metinler TUTORIAL.md §3'ten birebir gelir; panel kendi metnini yazmaz). */
export interface TutorialLessonView {
  /** 1'den başlayan ders numarası ("7 / 24"). */
  readonly index: number;
  readonly total: number;
  readonly title: string;
  /** Panel açıklaması (en çok 3 satır gösterilir). */
  readonly desc: string;
  /** Hedef satırı ("Mankene 3 kez tekme at."). Yoksa satır gizlenir. */
  readonly goal?: string;
  readonly keys?: readonly TutorialKeySpec[];
  /** Hedef adedi (sayaç "0/3"). 0 / verilmemiş → sayaç ve çubuk gizli (son ders: hedef yok). */
  readonly need?: number;
  /** Serbest antrenman: panel küçülür, yalnız başlık + tuş listesi kalır (§3 ders 24). */
  readonly mini?: boolean;
}
/** Hedef ilerlemesi; `need` yüzde hedefinde de aynı biçimde kullanılır ("18/40"). */
export interface TutorialProgress { readonly done: number; readonly need: number }

/** Panel zamanlamaları (TUTORIAL.md §4–§5; tuning.ts sim sayılarıdır, sunum süreleri burada yerel). */
export const TUT_HUD = {
  /** Kutlama metni açıklamanın yerinde kalır (s). */
  celebrateSec: 1.2,
  /** Panel kenarının yeşil parlaması (ms). */
  glowMs: 300,
  /** Ders değişiminde içeriğin çapraz solması (ms). */
  swapMs: 150,
  /** Sayaç "pop" büyümesi (ms) ve ölçeği. */
  popMs: 120, popScale: 1.15,
  /** Panelin açılıp kapanma süresi (ms). */
  fadeMs: 180,
  /** Bu genişliğin altında alt-orta yerleşim (px). */
  narrowPx: 900,
  /** Kaç panel senkronunda bir pad yoklanır (≈0.5 s @60 Hz). Bkz. `refreshPad`. */
  padPollTicks: 30,
} as const;

/**
 * Ders metinlerindeki girdi adı yer tutucusunu ({YonaIt} gibi) dolduran işlev.
 * NEDEN dışarıdan veriliyor: sözcük tablosu ders VERİSİNİN yanında (game/tutorial.ts `fillInput`), mimari ise tek yönlü —
 * view, game'den içe aktaramaz. Bağlı girdiyi yalnız panel bildiği için doldurma İŞLEMİ burada, tablo orada kalır:
 * `new TutorialHud(container, fillInput)`. Verilmezse metin olduğu gibi yazılır (panel tek başına da çalışır).
 */
export type TutorialTextFill = (text: string, padOn: boolean) => string;
const NO_FILL: TutorialTextFill = (text) => text;

const STYLE_ID = 'ringout-tutorial-style';
const CSS = `
.tut-root{position:fixed;inset:0;pointer-events:none;z-index:11;font:14px system-ui,sans-serif;color:#e6ebf3}
.tut-root *{box-sizing:border-box}
.tut-panel{position:absolute;left:24px;bottom:24px;width:min(420px,34%);min-height:190px;display:flex;flex-direction:column;gap:7px;
 background:rgba(10,13,19,.72);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:12px 14px;
 box-shadow:0 6px 24px rgba(0,0,0,.35);opacity:1;transform:translateY(0);transition:opacity ${TUT_HUD.fadeMs}ms,transform ${TUT_HUD.fadeMs}ms}
.tut-panel.off{opacity:0;transform:translateY(8px)}
.tut-panel.glow{border-color:#63e6be;box-shadow:0 0 0 1px rgba(99,230,190,.5),0 6px 24px rgba(0,0,0,.35)}
.tut-panel.mini{min-height:0}
.tut-panel.mini .tut-desc,.tut-panel.mini .tut-goal,.tut-panel.mini .tut-count,.tut-panel.mini .tut-bar{display:none}
/* Ders değişiminde tek yönlü sol(ma)→beliriş: içerik anında yazılır, gecikmeli DOM yazımı yok. */
@keyframes tut-swap{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.tut-body.swap{animation:tut-swap ${TUT_HUD.swapMs}ms ease-out}
.tut-body{display:flex;flex-direction:column;gap:7px;min-height:0}
.tut-head{display:flex;align-items:baseline;gap:8px}
.tut-no{font-size:11px;letter-spacing:.04em;color:#ffd43b;opacity:.85;white-space:nowrap}
.tut-title{font-size:15px;font-weight:700;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.tut-count{font-size:15px;font-weight:700;font-variant-numeric:tabular-nums;color:#ffd43b;white-space:nowrap;transform-origin:right center}
.tut-count.done{color:#63e6be}
@keyframes tut-pop{from{transform:scale(${TUT_HUD.popScale})}to{transform:scale(1)}}
.tut-count.pop{animation:tut-pop ${TUT_HUD.popMs}ms ease-out}
.tut-bar{height:3px;border-radius:2px;background:rgba(255,255,255,.12);overflow:hidden}
.tut-bar>i{display:block;height:100%;width:0;background:#ffd43b;border-radius:2px;transition:width .15s ease-out}
.tut-bar.done>i{background:#63e6be}
/* max-height satır sayısıyla UYUMLU olmalı: 3 × 1.35em = 4.05em. 3.2em iken üçüncü satır ORTADAN kesiliyordu
   (eğitim metninin son kelimesi kayboluyor) — entegrasyon bulgusu. */
.tut-desc{font-size:13px;line-height:1.35;opacity:.88;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden;max-height:4.15em}
.tut-desc.praise{opacity:1;color:#63e6be;font-weight:700;-webkit-line-clamp:2}
.tut-goal{font-size:13px;color:#ffe08a}
.tut-goal::before{content:"▸ ";opacity:.7}
.tut-keys{display:flex;flex-wrap:wrap;gap:4px 10px;align-items:center}
.tut-grp{display:flex;align-items:center;gap:5px}
.tut-lbl{font-size:11px;opacity:.55}
.tut-hold{font-size:11px;opacity:.6}
/* TEK GİRDİ KURALI: panelde yalnız BAĞLI girdinin rozetleri görünür. NEDEN: ikisi birden çizilince 9. ders
   "J · J · J · K   X · X · X · Y" oluyor ve çocuk bunu TEK bir sekiz tuşluk dizi sanıyor (oyun testi bulgusu).
   Etiket/ayırıcı eklemek yerine karşılığı hiç göstermiyoruz: oyuncu elindeki şeyi görsün, ötekini değil.
   Öbek boş kalmasın diye iki emniyet: pad karşılığı yoksa (.nopad) klavye, klavye karşılığı yoksa (.nokb) pad kalır. */
.tut-set{display:none;align-items:center;gap:3px}
.tut-set.kb{display:flex}
.tut-grp.nokb .tut-set.pad{display:flex}
.tut-keys.padon .tut-set.kb{display:none}
.tut-keys.padon .tut-set.pad{display:flex}
.tut-keys.padon .tut-grp.nopad .tut-set.kb{display:flex}
.tut-k{display:inline-block;min-width:20px;padding:1px 6px;border-radius:5px;font:600 11px/1.6 ui-monospace,SFMono-Regular,monospace;
 text-align:center;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.22);border-bottom-width:2px;color:#e6ebf3}
.tut-set.pad .tut-k{border-radius:9px;background:rgba(255,212,59,.14);border-color:rgba(255,212,59,.45);color:#ffe9a8}
.tut-plus{font-size:10px;opacity:.45}
.tut-hint{font-size:12px;color:#ffd43b;min-height:1.25em;line-height:1.25;opacity:0;transition:opacity .2s}
.tut-hint.on{opacity:1}
.tut-hint.note{color:#e6ebf3;opacity:.85}
@keyframes tut-flash{0%,100%{opacity:1}50%{opacity:.15}}
.tut-hint.flash{animation:tut-flash .5s 2}
.tut-foot{margin-top:auto;font-size:11px;opacity:.42;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* Dar pencere: panel alt ORTAYA geçer — ama HUD dövüşçü kartları da orada (rh-cards bottom 12px, ~76 px kuşak):
   panel kartların ÜSTÜNE değil, ÜSTÜNDE durur (bottom 100px). Açıklama yine 3 satır: eğitim cümlesi yarıda kesilmesin. */
.tut-root.narrow .tut-panel{left:50%;right:auto;bottom:100px;transform:translateX(-50%);width:min(560px,calc(100% - 32px));min-height:150px}
.tut-root.narrow .tut-panel.off{transform:translateX(-50%) translateY(8px)}
.tut-root.narrow .tut-desc{-webkit-line-clamp:3;max-height:4.15em}
@media (prefers-reduced-motion:reduce){.tut-panel,.tut-body,.tut-count,.tut-hint,.tut-bar>i{transition:none!important;animation:none!important}}
`;

/** Tuş öbeği kısa yazımını normalleştirir (düz metin = tek klavye rozeti). */
function asHint(k: TutorialKeySpec): TutorialKeyHint { return typeof k === 'string' ? { kb: [k] } : k; }

/**
 * Eğitim paneli. Sade ve olay üretmez:
 *   const hud = new TutorialHud(container, fillInput);   // ikinci argüman: {yon} yer tutucularını dolduran işlev
 *   hud.show(lesson);            // ders değişimi (içerik çapraz solar)
 *   hud.setProgress(2, 3);       // her doğru tekrarda (sayaç pop + çubuk)
 *   hud.showHint('...');         // takılınca; aynı metinle ikinci çağrı yanıp söner
 *   hud.celebrate('Harika!');    // ders tamamlanınca (kenar yeşil parlar, 1.2 s sonra açıklama geri gelir)
 *   hud.hide(); hud.dispose();
 */
export class TutorialHud {
  readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly noEl: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly countEl: HTMLDivElement;
  private readonly barEl: HTMLDivElement;
  private readonly barFill: HTMLElement;
  private readonly descEl: HTMLDivElement;
  private readonly goalEl: HTMLDivElement;
  private readonly keysEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;
  private readonly footEl: HTMLDivElement;

  /** Son yazılan değerler: setProgress her karede çağrılabilir, DOM'a yalnız değişince yazılır. */
  private lastDone = -1; private lastNeed = -1;
  /** HAM metinler (yer tutucular dolmadan): pad takılınca ekrandaki yazı bunlardan yeniden üretilir. */
  private hintText = ''; private noteRestore = '';
  private titleRaw = ''; private descRaw = ''; private goalRaw = ''; private hintRaw = ''; private praiseRaw = '';
  private visible = false;
  private padOn = false;
  private narrow = false;
  /** Pad yoklama sayacı (bkz. `setProgress`). */
  private padPoll = 0;
  private tCelebrate = 0; private tGlow = 0; private tHide = 0; private tNote = 0;
  private ro: ResizeObserver | null = null;
  private fillFn: TutorialTextFill;
  private readonly onPad = (): void => this.refreshPad();
  private readonly onWinResize = (): void => this.measure();

  constructor(container?: HTMLElement, fill?: TutorialTextFill) {
    this.fillFn = fill ?? NO_FILL;
    const doc = document;
    if (!doc.getElementById(STYLE_ID)) { const st = doc.createElement('style'); st.id = STYLE_ID; st.textContent = CSS; doc.head.appendChild(st); }
    const mk = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string): HTMLElementTagNameMap[K] => {
      const el = doc.createElement(tag); el.className = cls; return el;
    };
    this.root = mk('div', 'tut-root');
    this.panel = mk('div', 'tut-panel off');
    this.panel.style.display = 'none';
    this.body = mk('div', 'tut-body');
    const head = mk('div', 'tut-head');
    this.noEl = mk('div', 'tut-no');
    this.titleEl = mk('div', 'tut-title');
    this.countEl = mk('div', 'tut-count');
    head.append(this.noEl, this.titleEl, this.countEl);
    this.barEl = mk('div', 'tut-bar');
    this.barFill = doc.createElement('i'); this.barEl.appendChild(this.barFill);
    this.descEl = mk('div', 'tut-desc');
    this.goalEl = mk('div', 'tut-goal');
    this.keysEl = mk('div', 'tut-keys');
    this.body.append(head, this.barEl, this.descEl, this.goalEl, this.keysEl);
    this.hintEl = mk('div', 'tut-hint');
    // Ekran okuyucu / erişilebilirlik: ipucu ve sayaç sessizce değişmesin, ama dövüşün üstüne konuşmasın → politely.
    this.hintEl.setAttribute('aria-live', 'polite');
    this.footEl = mk('div', 'tut-foot');
    this.footEl.textContent = 'N atla · B geri · R baştan · Esc menü';
    this.panel.append(this.body, this.hintEl, this.footEl);
    this.root.appendChild(this.panel);
    (container ?? doc.body).appendChild(this.root);

    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width;
        this.setNarrow((w ?? this.root.clientWidth) < TUT_HUD.narrowPx);
      });
      this.ro.observe(this.root);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWinResize);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('gamepadconnected', this.onPad);
      window.addEventListener('gamepaddisconnected', this.onPad);
    }
    this.refreshPad();
  }

  /* ---------------------------------------------------------------- genel API */

  /** Dersi gösterir (panel kapalıysa açar). `progress` verilmezse 0 / lesson.need ile başlar. */
  show(lesson: TutorialLessonView, progress?: TutorialProgress): void {
    this.clearTimers();
    this.panel.classList.remove('glow');
    this.refreshPad();                      // ders arasında pad takılmış olabilir: metin ve rozetler ona göre yazılsın
    this.noEl.textContent = `${lesson.index} / ${lesson.total}`;
    this.titleRaw = lesson.title;
    this.titleEl.textContent = this.fill(this.titleRaw);
    this.descRaw = lesson.desc;
    this.goalRaw = lesson.goal ?? '';
    this.praiseRaw = '';
    this.descEl.classList.remove('praise');
    this.descEl.textContent = this.fill(this.descRaw);
    this.goalEl.textContent = this.fill(this.goalRaw);
    this.goalEl.style.display = lesson.goal ? '' : 'none';
    this.panel.classList.toggle('mini', lesson.mini === true);
    this.renderKeys(lesson.keys ?? []);
    this.setHintRaw('', false, false);
    this.lastDone = -1; this.lastNeed = -1;
    this.setProgress(progress?.done ?? 0, progress?.need ?? lesson.need ?? 0);
    restart(this.body, 'swap');             // §5: içerik 150 ms'de çapraz solar
    this.open();
  }

  /** Hedef sayacı ve çubuk. `need <= 0` → sayaç ve çubuk gizli (hedefsiz ders). */
  setProgress(done: number, need: number): void {
    // Pad yoklaması buraya asılı: panel her sim tick'inde senkronlanır ama TutorialPanel'de ayrı bir "kare" kanalı yok.
    // NEDEN gerekli: 'gamepadconnected' olayı bazı tarayıcılarda ancak bir tuşa basılınca gelir — ders ORTASINDA kol
    // takan çocuk rozetlerin (ve {yon} sözcüğünün) bir sonraki derse kadar değişmediğini görüyordu (oyun testi bulgusu).
    if (++this.padPoll >= TUT_HUD.padPollTicks) { this.padPoll = 0; this.refreshPad(); }
    const n = Math.max(0, Math.round(need));
    const d = Math.max(0, Math.min(n || done, Math.round(done)));
    if (d === this.lastDone && n === this.lastNeed) return;
    const grew = n === this.lastNeed && d > this.lastDone;   // yalnız gerçek ilerlemede pop (ders değişiminde değil)
    this.lastDone = d; this.lastNeed = n;
    if (n <= 0) {
      this.countEl.textContent = ''; this.countEl.style.display = 'none'; this.barEl.style.display = 'none';
      return;
    }
    this.countEl.style.display = ''; this.barEl.style.display = '';
    this.countEl.textContent = `${d}/${n}`;
    this.barFill.style.width = `${Math.round(Math.min(1, d / n) * 100)}%`;
    const full = d >= n;   // toggle idempotent: ders değişiminde önceki dersten kalan 'done' sınıfı da böyle temizlenir
    this.countEl.classList.toggle('done', full); this.barEl.classList.toggle('done', full);
    if (grew) restart(this.countEl, 'pop');
  }

  /** Takılma ipucu (§5). Aynı metinle ikinci çağrı satırı yanıp söndürür — yeni ipucu yazmadan dikkat çeker. */
  showHint(text: string): void {
    const same = text !== '' && text === this.hintText;
    this.setHintRaw(text, false, same);
  }
  /** İpucu satırını gizler. */
  clearHint(): void { this.setHintRaw('', false, false); }

  /** Metin doldurucuyu sonradan bağlar (panel kurulduktan sonra); ekrandaki yazı hemen yenilenir. */
  setTextFill(fill: TutorialTextFill): void { this.fillFn = fill; this.retext(); }

  /** Kısa nötr bilgi satırı (ör. "Düştün, hemen geri geldin. Devam."); süre bitince varsa ipucu geri gelir. */
  note(text: string, sec = 2): void {
    this.clearTimeout(this.tNote); this.tNote = 0;
    this.noteRestore = this.hintText;
    this.setHintRaw(text, true, false);
    this.tNote = setTimer(() => { this.tNote = 0; this.setHintRaw(this.noteRestore, false, false); }, sec * 1000);
  }

  /** Ders tamamlandı: açıklamanın yerinde kısa övgü + kenar yeşil parlar; 1.2 s sonra açıklama geri gelir (§5). */
  celebrate(text: string): void {
    this.clearTimeout(this.tCelebrate); this.clearTimeout(this.tGlow);
    this.praiseRaw = text;
    this.descEl.textContent = this.fill(text);
    this.descEl.classList.add('praise');
    this.panel.classList.add('glow');
    this.tGlow = setTimer(() => { this.tGlow = 0; this.panel.classList.remove('glow'); }, TUT_HUD.glowMs);
    this.tCelebrate = setTimer(() => {
      this.tCelebrate = 0;
      this.praiseRaw = '';
      this.descEl.classList.remove('praise');
      this.descEl.textContent = this.fill(this.descRaw);
    }, TUT_HUD.celebrateSec * 1000);
  }

  /** Paneli yumuşak kapatır (duraklatma / eğitimden çıkış). İçerik korunur; show() ile geri açılır. */
  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.clearTimeout(this.tHide);
    this.panel.classList.add('off');
    this.tHide = setTimer(() => { this.tHide = 0; this.panel.style.display = 'none'; }, TUT_HUD.fadeMs);
  }

  dispose(): void {
    this.clearTimers();
    this.ro?.disconnect(); this.ro = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('gamepadconnected', this.onPad);
      window.removeEventListener('gamepaddisconnected', this.onPad);
      window.removeEventListener('resize', this.onWinResize);
    }
    this.root.remove();
  }

  /* ---------------------------------------------------------------- iç */

  private open(): void {
    this.clearTimeout(this.tHide); this.tHide = 0;
    if (this.visible) return;
    this.visible = true;
    this.panel.style.display = '';
    // Geçiş çalışsın diye 'off' bir sonraki karede kalkar (aynı karede kaldırılırsa tarayıcı geçişi atlar).
    requestFrame(() => { if (this.visible) this.panel.classList.remove('off'); });
  }

  private setHintRaw(text: string, isNote: boolean, flash: boolean): void {
    if (!isNote) this.hintText = text;
    this.hintRaw = text;
    this.hintEl.textContent = this.fill(text);
    this.hintEl.classList.toggle('on', text !== '');
    this.hintEl.classList.toggle('note', isNote);
    if (flash) restart(this.hintEl, 'flash'); else this.hintEl.classList.remove('flash');
  }

  /** Yer tutucuları o anki girdiye göre doldurur (bkz. `TutorialTextFill`). */
  private fill(text: string): string { return this.fillFn(text, this.padOn); }

  private renderKeys(keys: readonly TutorialKeySpec[]): void {
    const doc = document;
    this.keysEl.replaceChildren();
    for (const spec of keys) {
      const k = asHint(spec);
      const hasKb = k.kb.length > 0, hasPad = k.pad !== undefined && k.pad.length > 0;
      const grp = doc.createElement('div');
      // Görünürlük CSS'te: bağlı olmayan girdinin rozeti çizilir ama gizlenir (pad takılınca yeniden çizim gerekmesin).
      grp.className = `tut-grp${hasPad ? '' : ' nopad'}${hasKb ? '' : ' nokb'}`;
      if (k.label) { const l = doc.createElement('span'); l.className = 'tut-lbl'; l.textContent = k.label; grp.appendChild(l); }
      if (hasKb) grp.appendChild(badgeSet(k.kb, 'kb'));
      if (hasPad) grp.appendChild(badgeSet(k.pad ?? [], 'pad'));
      if (k.hold) { const h = doc.createElement('span'); h.className = 'tut-hold'; h.textContent = 'basılı tut'; grp.appendChild(h); }
      this.keysEl.appendChild(grp);
    }
    this.keysEl.style.display = keys.length > 0 ? '' : 'none';
  }

  /**
   * Pad bağlı mı? Rozetlerin hangisinin görüneceği ve metinlerdeki `{yon}` sözcüğü buna bağlı.
   * Olay tabanlı (gamepadconnected) + `setProgress`ten seyrek yoklama; getGamepads bazı bağlamlarda atabilir → sarmalanır.
   */
  refreshPad(): void {
    let on = false;
    try {
      const list = typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : null;
      if (list) for (let i = 0; i < list.length; i++) { const g = list[i]; if (g && g.connected) { on = true; break; } }
    } catch { on = false; }
    if (on === this.padOn) return;
    this.padOn = on;
    this.keysEl.classList.toggle('padon', on);
    this.retext();
  }

  /** Girdi değişince ekrandaki yazıyı ham metinlerden yeniden üretir (ders ortasında kol takılabilir). */
  private retext(): void {
    const praising = this.descEl.classList.contains('praise');
    this.titleEl.textContent = this.fill(this.titleRaw);
    this.descEl.textContent = this.fill(praising ? this.praiseRaw : this.descRaw);
    this.goalEl.textContent = this.fill(this.goalRaw);
    this.hintEl.textContent = this.fill(this.hintRaw);
  }

  private measure(): void {
    const w = this.root.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : TUT_HUD.narrowPx);
    this.setNarrow(w < TUT_HUD.narrowPx);
  }
  private setNarrow(narrow: boolean): void {
    if (narrow === this.narrow) return;
    this.narrow = narrow;
    this.root.classList.toggle('narrow', narrow);
  }

  private clearTimeout(id: number): void { if (id && typeof window !== 'undefined') window.clearTimeout(id); }
  private clearTimers(): void {
    this.clearTimeout(this.tCelebrate); this.clearTimeout(this.tGlow); this.clearTimeout(this.tNote);
    this.tCelebrate = 0; this.tGlow = 0; this.tNote = 0;
  }
}

/* ---------------------------------------------------------------- yardımcılar */

/** Tek satırlık rozet dizisi: J · J · J (klavye) ya da X · X · X (pad). */
function badgeSet(labels: readonly string[], kind: 'kb' | 'pad'): HTMLSpanElement {
  const doc = document;
  const set = doc.createElement('span'); set.className = `tut-set ${kind}`;
  for (let i = 0; i < labels.length; i++) {
    if (i > 0) { const s = doc.createElement('span'); s.className = 'tut-plus'; s.textContent = '·'; set.appendChild(s); }
    const b = doc.createElement('kbd'); b.className = 'tut-k'; b.textContent = labels[i] ?? '';
    set.appendChild(b);
  }
  return set;
}
/** CSS animasyonunu yeniden tetikler (sınıf → reflow → sınıf). Seyrek çağrılır (ders/tekrar başına bir kez). */
function restart(el: HTMLElement, cls: string): void {
  el.classList.remove(cls);
  void el.offsetWidth;
  el.classList.add(cls);
}
function setTimer(fn: () => void, ms: number): number {
  return typeof window !== 'undefined' ? window.setTimeout(fn, ms) : 0;
}
function requestFrame(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn); else fn();
}
